import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { useT, LangToggle } from "../i18n/LanguageContext";

const LOGO = "https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/3m4xmkko_mother%20hippo%20PNG.png";

const copy = {
  en: {
    tag: "Design Showcase · 2026",
    badge: "Three Directions · One Mission",
    heroA: "Mother Hippo",
    heroB: "Foundation",
    sub: "Three exploratory website directions for a movement that protects girls, survives stigma, and ends Female Genital Mutilation — explore each design and pick the voice that carries the mission furthest.",
    pillars: ["FGM Awareness", "Advocacy", "Bodily Autonomy"],
    cards: [
      {
        num: "01",
        name: "Editorial & Storytelling",
        tag: "Magazine-inspired. Narrative first.",
        desc: "A dignified, cover-story aesthetic: drop-caps, asymmetric spreads, serif headlines, and slow-reveal imagery. Inspired by long-form journalism and luxury editorial.",
        tags: ["Cormorant Garamond", "Warm Cream", "Asymmetric"],
      },
      {
        num: "02",
        name: "Bold Activist",
        tag: "Manifesto. Unapologetic. Urgent.",
        desc: "High-contrast protest-poster energy: oversized condensed type, kinetic marquees, red & green blocks, and sharp borders. Built to move people to action.",
        tags: ["Anton", "Black Canvas", "Brutalist Grid"],
      },
      {
        num: "03",
        name: "Minimal Professional",
        tag: "Institutional. Data-led. Trustworthy.",
        desc: "A clean, NGO/UN-style system: generous white space, quiet typography, structured columns, and elegant impact metrics. Designed for funders and policy partners.",
        tags: ["Outfit + Work Sans", "White Canvas", "Structured"],
      },
      {
        num: "04",
        name: "Curated Programme",
        tag: "Museum-grade. Programmatic. Intellectual.",
        desc: "Inspired by LAS Art Foundation — a curatorial system of programmes, events and publications with metadata-led cards, restrained typography, and an almost institutional calm.",
        tags: ["Space Grotesk", "Off-White", "Metadata Grid"],
      },
      {
        num: "05",
        name: "Human Stories",
        tag: "Documentary. Family-first. Transformative.",
        desc: "Inspired by Navajo Water Project — a warm, photo-forward story of change: large images, before/after rhythm, a signed pledge, and a persistent give-back CTA.",
        tags: ["Merriweather + Figtree", "Earth Canvas", "Photo-led"],
      },
    ],
    footer: "Static Preview",
    footer2: "No Live Data",
    foundation: "motherhippo.org · Design Prototypes",
  },
  fr: {
    tag: "Vitrine Design · 2026",
    badge: "Trois Directions · Une Mission",
    heroA: "Mother Hippo",
    heroB: "Foundation",
    sub: "Trois directions web pour un mouvement qui protège les filles, survit à la stigmatisation et met fin aux Mutilations Génitales Féminines — explorez chaque design et choisissez la voix qui portera la mission le plus loin.",
    pillars: ["Sensibilisation MGF", "Plaidoyer", "Autonomie Corporelle"],
    cards: [
      {
        num: "01",
        name: "Éditorial & Récit",
        tag: "Inspiré du magazine. Le récit d'abord.",
        desc: "Une esthétique de couverture digne : lettrines, mises en page asymétriques, titres sérif et imagerie à révélation lente. Inspiré du journalisme long format et de l'édition de luxe.",
        tags: ["Cormorant Garamond", "Crème Chaude", "Asymétrique"],
      },
      {
        num: "02",
        name: "Activiste Audacieux",
        tag: "Manifeste. Sans compromis. Urgent.",
        desc: "Énergie d'affiche de manifestation à fort contraste : typographie condensée surdimensionnée, marquises cinétiques, blocs rouges et verts, bordures nettes. Conçu pour passer à l'action.",
        tags: ["Anton", "Toile Noire", "Grille Brutaliste"],
      },
      {
        num: "03",
        name: "Minimal Professionnel",
        tag: "Institutionnel. Axé données. Fiable.",
        desc: "Un système épuré façon ONG/ONU : espaces généreux, typographie discrète, colonnes structurées et indicateurs d'impact élégants. Conçu pour les bailleurs et partenaires politiques.",
        tags: ["Outfit + Work Sans", "Toile Blanche", "Structuré"],
      },
      {
        num: "04",
        name: "Programme Curatif",
        tag: "Niveau musée. Programmatique. Intellectuel.",
        desc: "Inspiré de LAS Art Foundation — un système curatorial de programmes, événements et publications avec cartes à métadonnées, typographie retenue et calme quasi-institutionnel.",
        tags: ["Space Grotesk", "Blanc Cassé", "Grille Métadonnées"],
      },
      {
        num: "05",
        name: "Récits Humains",
        tag: "Documentaire. Famille d'abord. Transformateur.",
        desc: "Inspiré du Navajo Water Project — un récit de changement chaleureux et photo-centré : grandes images, rythme avant/après, un engagement signé et un appel à l'action persistant.",
        tags: ["Merriweather + Figtree", "Toile Terre", "Photo-centré"],
      },
    ],
    footer: "Aperçu Statique",
    footer2: "Aucune Donnée Live",
    foundation: "motherhippo.org · Prototypes Design",
  },
};

const accents = ["#00A651", "#E3282B", "#D4A93B", "#0F172A", "#8C5A3C"];
const previews = [
  "https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/cn9nk1cb_Screenshot%202026-04-30%20at%201.12.37%E2%80%AFPM.png",
  "https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/1avalk56_Screenshot%202026-04-30%20at%201.12.32%E2%80%AFPM.png",
  "https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/4b9z0d2l_Screenshot%202026-04-30%20at%201.12.43%E2%80%AFPM.png",
  "https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/1avalk56_Screenshot%202026-04-30%20at%201.12.32%E2%80%AFPM.png",
  "https://customer-assets.emergentagent.com/job_equality-hub-7/artifacts/4b9z0d2l_Screenshot%202026-04-30%20at%201.12.43%E2%80%AFPM.png",
];
const slugs = ["/prototype-1", "/prototype-2", "/prototype-3", "/prototype-4", "/prototype-5"];

export default function Landing() {
  const t = useT(copy);

  return (
    <div
      data-testid="landing-page"
      className="min-h-screen bg-black text-white font-grotesk relative overflow-hidden"
    >
      {/* Bold ambient glows */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -left-40 h-[620px] w-[620px] rounded-full blur-3xl opacity-50"
        style={{ background: "radial-gradient(circle, #00A651 0%, transparent 60%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/3 -right-40 h-[560px] w-[560px] rounded-full blur-3xl opacity-40"
        style={{ background: "radial-gradient(circle, #E3282B 0%, transparent 60%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 left-1/3 h-[520px] w-[520px] rounded-full blur-3xl opacity-40"
        style={{ background: "radial-gradient(circle, #D4A93B 0%, transparent 60%)" }}
      />

      {/* Header */}
      <header
        data-testid="landing-header"
        className="relative z-10 flex items-center justify-between px-6 md:px-12 pt-8"
      >
        <img
          src={LOGO}
          alt="Mother Hippo Foundation"
          className="h-12 md:h-14 w-auto object-contain"
          data-testid="landing-logo"
        />
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-white/70">
            <Sparkles size={14} className="text-[#00A651]" />
            {t.tag}
          </div>
          <LangToggle variant="dark" testId="landing-lang-toggle" />
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-6 md:px-12 pt-20 md:pt-28 pb-12 md:pb-20 max-w-7xl mx-auto">
        <div className="mh-rise">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#E3282B]/60 bg-[#E3282B]/10 text-[11px] tracking-[0.25em] uppercase text-white">
            <span className="h-1.5 w-1.5 rounded-full bg-[#E3282B]" />
            {t.badge}
          </div>
        </div>
        <h1
          className="mh-rise mh-delay-1 mt-6 font-outfit font-light text-white text-5xl sm:text-6xl md:text-7xl lg:text-[104px] leading-[0.95] tracking-tight"
          data-testid="landing-hero-title"
        >
          {t.heroA}
          <br />
          <span className="italic font-cormorant font-medium text-[#00A651]">
            {t.heroB}
          </span>
        </h1>
        <p
          className="mh-rise mh-delay-2 mt-8 max-w-2xl text-base md:text-lg text-white/80 leading-relaxed"
          data-testid="landing-hero-subtitle"
        >
          {t.sub}
        </p>
        <div className="mh-rise mh-delay-3 mt-10 flex items-center gap-4 text-xs tracking-[0.3em] uppercase text-white/70 flex-wrap">
          {t.pillars.map((p, i) => (
            <span key={i} className="flex items-center gap-4">
              <span className="inline-flex items-center gap-2">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ background: accents[i] }}
                />
                {p}
              </span>
              {i < t.pillars.length - 1 && <span className="h-px w-8 bg-white/20" />}
            </span>
          ))}
        </div>
      </section>

      {/* Prototype grid */}
      <section
        className="relative z-10 px-6 md:px-12 pb-24 max-w-7xl mx-auto"
        data-testid="prototype-grid"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {t.cards.map((p, i) => (
            <Link
              key={i}
              to={slugs[i]}
              data-testid={`prototype-card-${i + 1}`}
              className="group relative block rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 mh-rise"
              style={{
                animationDelay: `${i * 0.12 + 0.2}s`,
                boxShadow: `0 0 0 1px rgba(255,255,255,0.08)`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = `0 0 0 2px ${accents[i]}, 0 40px 80px -20px ${accents[i]}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = `0 0 0 1px rgba(255,255,255,0.08)`;
              }}
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={previews[i]}
                  alt={p.name}
                  className="absolute inset-0 h-full w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-105 group-hover:scale-100"
                />
                <div
                  className="absolute inset-0 transition-opacity duration-500"
                  style={{
                    background: `linear-gradient(to top, #000 0%, ${accents[i]}40 50%, transparent 100%)`,
                  }}
                />
                <div
                  className="absolute top-5 left-5 px-3 py-1 rounded-full text-[10px] tracking-[0.4em] uppercase text-white font-semibold"
                  style={{ background: accents[i] }}
                >
                  {p.num} · PROTOTYPE
                </div>
                <div
                  className="absolute bottom-5 right-5 flex items-center justify-center h-12 w-12 rounded-full border-2 bg-black/50 backdrop-blur-md transition-all duration-500 group-hover:scale-110"
                  style={{ borderColor: accents[i] }}
                >
                  <ArrowUpRight
                    size={20}
                    style={{ color: accents[i] }}
                    className="group-hover:rotate-45 transition-transform duration-500"
                  />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div
                    className="h-1 w-16 mb-4 rounded-full"
                    style={{ background: accents[i] }}
                  />
                  <h3 className="font-outfit text-2xl md:text-3xl font-semibold text-white leading-tight">
                    {p.name}
                  </h3>
                  <p className="mt-1 text-sm text-white/80 italic font-cormorant">
                    {p.tag}
                  </p>
                </div>
              </div>
              <div
                className="p-6 bg-black relative"
                style={{ borderTop: `2px solid ${accents[i]}` }}
              >
                <p className="text-sm text-white/75 leading-relaxed">{p.desc}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {p.tags.map((x) => (
                    <span
                      key={x}
                      className="text-[10px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-full border text-white/70"
                      style={{ borderColor: `${accents[i]}60` }}
                    >
                      {x}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 mt-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="" className="h-10 w-auto object-contain" />
            <div className="text-xs text-white/60 tracking-wider uppercase">{t.foundation}</div>
          </div>
          <div className="flex items-center gap-5 text-xs tracking-[0.25em] uppercase text-white/60">
            <span>{t.footer}</span>
            <span className="h-1 w-1 rounded-full bg-white/40" />
            <span>{t.footer2}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
